import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { useIFixitApiClient } from '@ifixit/ifixit-api-client';
import { Duration } from '@ifixit/utils';
import { useCacheableQuery } from '@ifixit/utils-react/hooks';
import { Badge } from '@ifixit/ui/components/badge';
import { Button } from '@ifixit/ui/components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ifixit/ui/components/dialog';
import { cn } from '@ifixit/ui/lib/utils';
import { _js } from '@ifixit/utils/localize';
import initializeComponent from 'Shared/react-initialize';
import { Recaptcha } from 'Shared/recaptcha';
import { useRef, useState, type FormEvent, type RefObject } from 'react';
import { z } from 'zod';
import { useLatestDefined } from 'Shared/hooks/use-latest-defined';

const MAX_DISTANCE_MILES = 50;

const ProShopSchema = z.object({
   id: z.string(),
   name: z.string(),
   address: z.string(),
   city: z.string(),
   state: z.string(),
   postcode: z.string(),
   country: z.string(),
   latitude: z.number(),
   longitude: z.number(),
   distanceMiles: z.number(),
   affiliation: z.enum(['microcenter', 'independent', 'repairdesk']),
   consultationUrl: z.string().optional(),
});
type ProShop = z.infer<typeof ProShopSchema>;

type Coordinates = { latitude: number; longitude: number };

type ProLeadSectionProps = {
   deviceName: string;
   guideTitle: string;
   guideid: number;
   imageUrl: string;
   userName: string | null;
   userEmail: string | null;
   recaptchaSiteKey: string | null;
};

function ProLeadSection({
   deviceName,
   guideTitle,
   guideid,
   imageUrl,
   userName,
   userEmail,
   recaptchaSiteKey,
}: ProLeadSectionProps) {
   const [coords, setCoords] = useState<Coordinates | null>(debugCoords());
   const closestShop = useClosestProShop({
      latitude: coords?.latitude ?? null,
      longitude: coords?.longitude ?? null,
   });
   const firstShop = useLatestDefined(closestShop);
   const [hasInteracted, setHasInteracted] = useState(false);
   const [dialogOpen, setDialogOpen] = useState(false);
   const recaptchaRef = useRef<HTMLDivElement>(null);

   if (!firstShop || (!hasInteracted && firstShop.distanceMiles > MAX_DISTANCE_MILES)) {
      return null;
   }

   const onOpen = () => {
      trackShopClick({ shop: firstShop, action: 'Find Repair Shop Click' });
      navigator.geolocation.getCurrentPosition(
         position => {
            setCoords(position.coords);
         },
         positionError => {
            console.error(
               'Failed to get current position for repair shop locator',
               positionError.code,
               positionError.message
            );
         },
         { enableHighAccuracy: true, timeout: 5000, maximumAge: 3_600_000 }
      );
   };

   return (
      <>
         <ShopCard
            shop={firstShop}
            imageUrl={imageUrl}
            onRequestRepair={() => {
               setHasInteracted(true);
               setDialogOpen(true);
               onOpen();
            }}
            onScheduleNow={() => {
               trackShopClick({ shop: firstShop, action: 'Schedule Now Click' });
            }}
         />
         {dialogOpen && (
            <LeadFormDialog
               shop={firstShop}
               deviceName={deviceName}
               guideTitle={guideTitle}
               guideid={guideid}
               userName={userName}
               userEmail={userEmail}
               recaptchaSiteKey={recaptchaSiteKey}
               recaptchaRef={recaptchaRef}
               onClose={() => setDialogOpen(false)}
            />
         )}
         <div ref={recaptchaRef} className="hidden" />
      </>
   );
}

type ShopCardProps = {
   shop: ProShop;
   imageUrl: string;
   onRequestRepair: () => void;
   onScheduleNow: () => void;
};

function ShopCard({ shop, imageUrl, onRequestRepair, onScheduleNow }: ShopCardProps) {
   return (
      <div className="container mx-auto my-6 rounded-xl w-full overflow-hidden grid grid-rows-2 md:grid-cols-2 md:grid-rows-none">
         <div className="p-4 flex flex-col items-start justify-center gap-4 bg-gray-100 md:py-8">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
               {_js('Find a Repair Shop')}
            </p>
            <div className="flex flex-col gap-1">
               <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-gray-900">{shop.name}</h3>
                  <Badge className="badge-brand">
                     {_js('%1 mi', Math.round(shop.distanceMiles).toString())}
                  </Badge>
               </div>
               <p className="text-sm text-gray-600">
                  {shop.address}, {shop.city}, {shop.state} {shop.postcode}
               </p>
            </div>
            {shop.affiliation === 'microcenter' ? (
               <Button asChild>
                  <a
                     href={shop.consultationUrl}
                     target="_blank"
                     rel="noreferrer"
                     onClick={onScheduleNow}
                  >
                     {_js('Schedule Now')}
                  </a>
               </Button>
            ) : (
               <Button onClick={onRequestRepair}>{_js('Request a Repair')}</Button>
            )}
         </div>
         <div className="relative overflow-hidden">
            <img src={imageUrl} alt="" className="absolute inset-0 size-full object-cover" />
         </div>
      </div>
   );
}

type LeadFormDialogProps = {
   shop: ProShop;
   deviceName: string;
   guideTitle: string;
   guideid: number;
   userName: string | null;
   userEmail: string | null;
   recaptchaSiteKey: string | null;
   recaptchaRef: RefObject<HTMLDivElement | null>;
   onClose: () => void;
};

function LeadFormDialog({
   shop,
   deviceName,
   guideTitle,
   guideid,
   userName,
   userEmail,
   recaptchaSiteKey,
   recaptchaRef,
   onClose,
}: LeadFormDialogProps) {
   const api = useIFixitApiClient();
   const [submitting, setSubmitting] = useState(false);
   const [submitted, setSubmitted] = useState(false);
   const [error, setError] = useState<string | null>(null);

   const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setSubmitting(true);
      setError(null);

      const formData = new FormData(e.currentTarget);

      try {
         let captchaResult: string | undefined;
         if (recaptchaSiteKey) {
            captchaResult = await Recaptcha.executeInvisible(
               recaptchaRef.current,
               recaptchaSiteKey,
               'pro-lead'
            );
         }

         await api.post('lead/submit', 'submit-pro-lead', {
            body: JSON.stringify({
               shopId: shop.id,
               name: formData.get('name'),
               email: formData.get('email'),
               phone: formData.get('phone') || undefined,
               device: formData.get('device') || undefined,
               repair: formData.get('repair') || undefined,
               notes: formData.get('notes') || undefined,
               guideid: guideid || undefined,
               captchaResult,
            }),
         });
         setSubmitted(true);
      } catch {
         setError(_js('Something went wrong. Please try again.'));
      } finally {
         setSubmitting(false);
      }
   };

   return (
      <Dialog
         open
         onOpenChange={open => {
            if (!open) onClose();
         }}
      >
         <DialogContent className="max-sm:top-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:max-w-full max-sm:h-dvh max-sm:rounded-none max-sm:overflow-y-auto">
            <DialogHeader>
               <DialogTitle>{_js('Request a Repair')}</DialogTitle>
            </DialogHeader>

            {submitted ? (
               <div className="py-8 text-center">
                  <p className="text-lg font-medium text-gray-900 mb-2">{_js('Request sent!')}</p>
                  <p className="text-gray-600">
                     {_js('Your selected shop will contact you soon to discuss the repair.')}
                  </p>
                  <Button onClick={onClose} className="mt-6">
                     {_js('Close')}
                  </Button>
               </div>
            ) : (
               <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="rounded-lg border border-gray-200 p-3">
                     <div className="flex items-center gap-3">
                        <h3 className="text-base font-semibold text-gray-900">{shop.name}</h3>
                        <Badge className="badge-brand">
                           {_js('%1 mi', Math.round(shop.distanceMiles).toString())}
                        </Badge>
                     </div>
                     <p className="text-sm text-gray-600 mt-1">
                        {shop.address}, {shop.city}, {shop.state} {shop.postcode}
                     </p>
                  </div>
                  <Field
                     label={_js('Device')}
                     name="device"
                     defaultValue={deviceName || undefined}
                  />
                  <Field
                     label={_js('Repair')}
                     name="repair"
                     defaultValue={guideTitle || undefined}
                  />
                  <div className="flex flex-col gap-1">
                     <label className="text-sm font-medium text-gray-700">{_js('Notes')}</label>
                     <textarea
                        name="notes"
                        rows={3}
                        maxLength={2000}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder={_js('Any additional details about your repair...')}
                     />
                  </div>
                  <Field
                     label={_js('Name')}
                     name="name"
                     defaultValue={userName ?? undefined}
                     required
                  />
                  <Field
                     label={_js('Email')}
                     name="email"
                     type="email"
                     defaultValue={userEmail ?? undefined}
                     required
                  />
                  <Field label={_js('Phone number')} name="phone" type="tel" />

                  {error && <p className="text-sm text-red-600">{error}</p>}

                  <p className="text-gray-500 text-center">
                     {_js('The shop will call or email you directly to discuss the repair.')}
                  </p>

                  <Button type="submit" disabled={submitting} className="mt-2">
                     {submitting ? _js('Submitting...') : _js('Submit Request')}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                     {_js(
                        'By submitting, you agree to our Privacy Policy and consent to receive email and text communications from iFixit and the selected repair shop, as applicable.'
                     )}
                  </p>
               </form>
            )}
         </DialogContent>
      </Dialog>
   );
}

type FieldProps = {
   label: string;
   name: string;
   defaultValue?: string;
   type?: string;
   required?: boolean;
};

function Field({ label, name, defaultValue, type = 'text', required }: FieldProps) {
   return (
      <div className="flex flex-col gap-1">
         <label className="text-sm font-medium text-gray-700">{label}</label>
         <input
            name={name}
            type={type}
            defaultValue={defaultValue}
            required={required}
            className={cn(
               'rounded-lg border border-gray-300 px-3 py-2 text-sm',
               'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
            )}
         />
      </div>
   );
}

function useClosestProShop({
   latitude,
   longitude,
}: {
   latitude: number | null;
   longitude: number | null;
}) {
   const api = useIFixitApiClient();

   const { data } = useCacheableQuery({
      cacheTime: Duration(1).day,
      schema: ProShopSchema.nullable(),
      storage: 'localStorage',
      queryKey: ['nearest-pro-shop', latitude, longitude],
      queryFn: async () => {
         const coordinates =
            latitude === null || longitude === null ? undefined : { latitude, longitude };
         return api.post<ProShop | null>('lead/nearest', 'get-nearest-pro-shop', {
            body: JSON.stringify({ coordinates }),
         });
      },
   });

   return data ?? null;
}

function debugCoords(): Coordinates | null {
   const searchParams = new URLSearchParams(window.location.search);
   const latitude = Number(searchParams.get('latitude') ?? 'nan');
   const longitude = Number(searchParams.get('longitude') ?? 'nan');
   if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
   return { latitude, longitude };
}

function trackShopClick({ shop, action }: { shop: ProShop; action: string }) {
   trackAnalyticsCustomEvent({
      eventCategory: 'Repair Shop Locator',
      eventAction: action,
      eventName: `${shop.name}, ${shop.city}`,
   });
}

initializeComponent('ProLeadSectionComponent', ProLeadSection);
